import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { ErrorState, FilterTray } from "../../resusableComponents/funnelFuel";

import { DIMENSIONS } from "./lib/constants";
import { adaptDashboard } from "./lib/adapter";
import { FilterProvider, useFilters } from "./lib/FilterContext";
import { applyFilters } from "./lib/filters";
import { parseFilters } from "./lib/filterUrl";
import { buildIndexes, buildView } from "./lib/selectors";
import { useFilterUrlSync } from "./lib/useFilterUrlSync";

import { AssetsPanel } from "./panels/AssetsPanel";
import { AudiencePanel } from "./panels/AudiencePanel";
import { CompaniesPanel } from "./panels/CompaniesPanel";
import { LeadRegisterPanel } from "./panels/LeadRegisterPanel";
import { LineItemsPanel } from "./panels/LineItemsPanel";
import { PacePanel } from "./panels/PacePanel";

/*
  FunnelFuel live campaign dashboard (RH-1811): the six panels shown under CLASSIC on /campaigns/dashboard/:id when
  the API says `dashboardType: "funnelfuel"` (the page itself, its header card and the Classic/Power BI switch belong
  to pages/CampaignDashboard). Everything after the page's one fetch, filtering included, is JavaScript over
  `model.leads`, so a filter change never refetches or shows a loader.

  `data` is the raw `responseData` the existing dashboard call already put in redux; the adapter is the only code
  that reads it.
*/

const Body = ({ model, droppedCount }) => {
  const intl = useIntl();
  const { filters, active, remove, clear } = useFilters();

  const indexes = useMemo(() => buildIndexes(model), [model]);
  /* The filter is applied ONCE, here, and the result passed down. Applying it inside each panel would do the same
     work six times. With no filters it returns the same array, so everything below stays memoised. */
  const rows = useMemo(() => applyFilters(model.leads, filters), [model.leads, filters]);
  const view = useMemo(() => buildView(model, rows, indexes), [model, rows, indexes]);

  const { truncated } = useFilterUrlSync(filters);

  const unknownCompany = intl.formatMessage({ id: "ff_dashboard.unknown_company", defaultMessage: "Unknown company" });

  const chips = useMemo(() => {
    const { lookups } = model;
    const names = { lineItemId: lookups.lineItemName, companyKey: lookups.companyName, assetKey: lookups.assetName };

    // Display names, never raw identifiers.
    return DIMENSIONS.filter((dimension) => filters[dimension]).flatMap((dimension) => (
      [...filters[dimension]].map((value) => ({
        dimension,
        value,
        dimensionLabel: intl.formatMessage({ id: `ff_dashboard.dim.${dimension}`, defaultMessage: dimension }),
        label: (names[dimension] && names[dimension][value]) || (dimension === "companyKey" ? unknownCompany : value)
      }))
    ));
  }, [filters, model, intl, unknownCompany]);

  const notices = [];
  if (droppedCount > 0) {
    notices.push(intl.formatMessage({ id: "ff_dashboard.tray.notice_dropped", defaultMessage: "Some filters in this link are no longer available and were removed." }));
  }
  if (truncated) {
    notices.push(intl.formatMessage({ id: "ff_dashboard.tray.notice_truncated", defaultMessage: "This selection is too long to save in the page link, so some filters are not included in it." }));
  }

  const { visibility } = model;

  return (
    <div className="ff-dash">
      <FilterTray chips={chips} total={view.totalAll} shown={view.total} onRemove={remove} onClear={clear} notices={notices} />

      <div className="ff-panels">
        {/* Panels the campaign cannot fill are not rendered at all, so the page closes up with no gap. */}
        {visibility.pace && <PacePanel model={model} view={view} active={active} />}
        {visibility.lineItems && <LineItemsPanel model={model} view={view} />}
        {visibility.companies && <CompaniesPanel view={view} />}
        {visibility.audience && <AudiencePanel view={view} cuts={model.audienceCuts} />}
        {visibility.assets && <AssetsPanel model={model} view={view} />}
        {visibility.leadRegister && <LeadRegisterPanel model={model} view={view} active={active} />}
      </div>
    </div>
  );
};

// Restores the selection from the URL once, after the data has arrived so it can be validated against it.
const LoadedDashboard = ({ model }) => {
  const location = useLocation();
  const restored = useMemo(() => parseFilters(location.search, model.knownValues), [model]);

  return (
    <FilterProvider initialFilters={restored.filters}>
      <Body model={model} droppedCount={restored.dropped} />
    </FilterProvider>
  );
};

const FunnelFuelDashboard = ({ campaignId, data, onRetry }) => {
  const intl = useIntl();

  // An unknown schemaVersion or a malformed payload is the error state, never a crash.
  const adapted = useMemo(() => {
    try {
      return { model: adaptDashboard(data) };
    } catch (error) {
      // The user sees the generic box; the reason goes to the console for developers.
      console.error(`FunnelFuel dashboard: ${error.code || "error"}: ${error.message}`);
      return { error };
    }
  }, [data]);

  if (adapted.error) {
    return (
      <div className="ff-dash">
        <ErrorState
          title={intl.formatMessage({ id: "ff_dashboard.error.title", defaultMessage: "This campaign could not be loaded" })}
          message={intl.formatMessage({ id: "ff_dashboard.error.body", defaultMessage: "The dashboard request failed. Nothing has been lost — try again, or contact your account manager if it keeps happening." })}
          onRetry={onRetry}
        />
      </div>
    );
  }

  // Data left in redux by another campaign is not this one's: render nothing until the response for this one lands.
  const { id } = adapted.model.campaign;
  if (id !== null && id !== String(campaignId)) { return null; }

  return <LoadedDashboard key={campaignId} model={adapted.model} />;
};

export default FunnelFuelDashboard;
